import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import API_CONFIG from "../../config/api";
import { useSelection } from "../../SelectionContext";
import {
  useLogisticsPermissions,
  type LogisticsPermissions,
} from "../hooks/useLogisticsPermissions";
import { TreasuryAccount, TreasuryMovement } from "../types/treasury.types";

const { RangePicker } = DatePicker;
const { Text } = Typography;
const { Option } = Select;

const manualCategories = [
  { value: "deposit", label: "Depot" },
  { value: "withdrawal", label: "Retrait" },
  { value: "advance", label: "Avance" },
  { value: "refund", label: "Remboursement" },
  { value: "adjustment", label: "Ajustement" },
];

const categoryLabels: Record<string, string> = {
  payment_customer: "Paiement client",
  payment_supplier: "Paiement fournisseur",
  deposit: "Depot",
  withdrawal: "Retrait",
  advance: "Avance",
  refund: "Remboursement",
  adjustment: "Ajustement",
  transfer_in: "Transfert entrant",
  transfer_out: "Transfert sortant",
};

const formatCurrency = (value: number | string, symbol?: string | null) => {
  const numericValue =
    typeof value === "string" ? parseFloat(value) : Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  const formatted = new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numericValue);
  return symbol ? `${formatted} ${symbol}` : formatted;
};

type LogisticsTreasuryMovementsProps = {
  active?: boolean;
  refreshKey?: number;
  permissions?: LogisticsPermissions;
  moduleScope?: "logistics" | "commercial" | "all";
};

const LogisticsTreasuryMovements: React.FC<LogisticsTreasuryMovementsProps> = ({
  active,
  refreshKey,
  permissions: permissionsOverride,
  moduleScope: moduleScopeOverride,
}) => {
  const { selectedCompany, selectedWarehouse } = useSelection();
  const fallbackPermissions = useLogisticsPermissions();
  const permissions = permissionsOverride ?? fallbackPermissions;
  const moduleScope = moduleScopeOverride ?? "logistics";

  const [accounts, setAccounts] = useState<TreasuryAccount[]>([]);
  const [data, setData] = useState<TreasuryMovement[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  });
  const [filters, setFilters] = useState<{
    dateRange: [Dayjs, Dayjs] | null;
    accountId: number | null;
    direction: "all" | "in" | "out";
    category: string;
    search: string;
  }>({
    dateRange: null,
    accountId: null,
    direction: "all",
    category: "all",
    search: "",
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchAccounts = useCallback(async () => {
    if (!selectedCompany || !selectedWarehouse) {
      setAccounts([]);
      return;
    }

    try {
      const response = await axios.get(
        `${API_CONFIG.API_URL}/treasury/accounts`,
        {
          params: {
            company_id: selectedCompany,
            warehouse_id: selectedWarehouse,
            include_inactive: true,
            module_scope: moduleScope,
          },
        }
      );
      if (response.data?.success) {
        setAccounts(response.data.data || []);
      }
    } catch (error) {
      console.error("[TREASURY] Error fetching accounts for movements:", error);
    }
  }, [selectedCompany, selectedWarehouse, moduleScope]);

  const buildParams = useCallback(() => {
    if (!selectedCompany || !selectedWarehouse) return null;
    const params: Record<string, string | number> = {
      company_id: selectedCompany,
      warehouse_id: selectedWarehouse,
      module_scope: moduleScope,
    };

    if (filters.accountId) {
      params.account_id = filters.accountId;
    }

    if (filters.direction !== "all") {
      params.direction = filters.direction;
    }

    if (filters.category !== "all") {
      params.category = filters.category;
    }

    if (filters.search.trim()) {
      params.search = filters.search.trim();
    }

    if (filters.dateRange) {
      params.start_date = filters.dateRange[0].format("YYYY-MM-DD");
      params.end_date = filters.dateRange[1].format("YYYY-MM-DD");
    }

    return params;
  }, [filters, selectedCompany, selectedWarehouse, moduleScope]);

  const fetchMovements = useCallback(async () => {
    const params = buildParams();
    if (!params) {
      setData([]);
      setPagination((prev) => ({ ...prev, total: 0, page: 1 }));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_CONFIG.API_URL}/treasury/movements`,
        {
          params: {
            ...params,
            page: pagination.page,
            limit: pagination.limit,
          },
        }
      );

      if (response.data?.success) {
        setData(response.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: response.data.pagination?.total || 0,
        }));
      } else {
        message.error(
          response.data?.message || "Erreur lors du chargement des mouvements"
        );
      }
    } catch (error: any) {
      console.error("[TREASURY] Error fetching movements:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors du chargement des mouvements"
      );
    } finally {
      setLoading(false);
    }
  }, [buildParams, pagination.limit, pagination.page]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    if (active) {
      fetchAccounts();
    }
  }, [active, fetchAccounts]);

  useEffect(() => {
    if (refreshKey !== undefined) {
      fetchAccounts();
    }
  }, [fetchAccounts, refreshKey]);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  const handleFilterChange = (partial: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...partial }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleTableChange = (page: number, pageSize: number) => {
    setPagination((prev) => ({ ...prev, page, limit: pageSize }));
  };

  const openCreateModal = () => {
    form.resetFields();
    form.setFieldsValue({
      direction: "in",
      category: "deposit",
      movement_date: dayjs(),
      amount: 0,
    });
    setModalOpen(true);
  };

  const handleCreate = async () => {
    if (!selectedCompany || !selectedWarehouse) {
      message.warning("Selectionnez une entreprise et un établissement.");
      return;
    }

    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        module_scope: moduleScope,
        account_id: values.account_id,
        direction: values.direction,
        category: values.category,
        amount: values.amount,
        movement_date: values.movement_date
          ? values.movement_date.format("YYYY-MM-DD")
          : undefined,
        reference: values.reference || undefined,
        description: values.description || undefined,
      };

      await axios.post(`${API_CONFIG.API_URL}/treasury/movements`, payload);
      message.success("Mouvement enregistre.");
      setModalOpen(false);
      fetchMovements();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error("[TREASURY] Error creating movement:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de l'enregistrement du mouvement"
      );
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => {
      const cols: any[] = [
        {
          title: "Date",
          dataIndex: "movement_date",
          key: "movement_date",
          render: (value: string) =>
            value ? dayjs(value).format("DD/MM/YYYY") : "-",
        },
        {
          title: "Compte",
          dataIndex: "account_name",
          key: "account_name",
          render: (value: string, record: TreasuryMovement) =>
            value || record.account_id,
        },
        {
          title: "Module",
          dataIndex: "module_scope",
          key: "module_scope",
          render: (value: string) =>
            value === "logistics"
              ? "Logistique"
              : value === "commercial"
              ? "Commercial"
              : value || "-",
        },
        {
          title: "Direction",
          dataIndex: "direction",
          key: "direction",
          render: (value: "in" | "out") =>
            value === "in" ? (
              <Tag color="green">Entree</Tag>
            ) : (
              <Tag color="red">Sortie</Tag>
            ),
        },
        {
          title: "Categorie",
          dataIndex: "category",
          key: "category",
          render: (value: string) => categoryLabels[value] || value,
        },
        {
          title: "Montant",
          dataIndex: "amount",
          key: "amount",
          align: "right" as const,
          render: (value: number, record: TreasuryMovement) =>
            formatCurrency(value, record.currency_symbol),
        },
        {
          title: "Solde apres mouvement",
          dataIndex: "balance_after",
          key: "balance_after",
          align: "right" as const,
          render: (value: number | string, record: TreasuryMovement) =>
            formatCurrency(value, record.currency_symbol),
        },
        {
          title: "Reference",
          dataIndex: "reference",
          key: "reference",
          ellipsis: true,
          render: (value: string) => value || "-",
        },
        {
          title: "Description",
          dataIndex: "description",
          key: "description",
          ellipsis: true,
          render: (value: string) => value || "-",
        },
      ];

      if (moduleScope !== "all") {
        cols.splice(2, 1);
      }

      return cols;
    },
    [moduleScope]
  );

  if (!selectedCompany || !selectedWarehouse) {
    return (
      <Card>
        <Alert
          message="Selection requise"
          description="Selectionnez une entreprise et un établissement pour afficher les mouvements."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <div>
      <Card className="mb-4">
        <Space wrap style={{ justifyContent: "space-between", width: "100%" }}>
          <Space wrap>
            <RangePicker
              value={filters.dateRange || undefined}
              onChange={(range) =>
                handleFilterChange({ dateRange: range as [Dayjs, Dayjs] | null })
              }
              format="DD/MM/YYYY"
            />
            <Select
              style={{ minWidth: 180 }}
              allowClear
              placeholder="Compte"
              value={filters.accountId || undefined}
              onChange={(value) =>
                handleFilterChange({ accountId: value || null })
              }
            >
              {accounts.map((account) => (
                <Option key={account.id} value={account.id}>
                  {account.name}
                </Option>
              ))}
            </Select>
            <Select
              style={{ minWidth: 150 }}
              value={filters.direction}
              onChange={(value) => handleFilterChange({ direction: value })}
              options={[
                { value: "all", label: "Toutes directions" },
                { value: "in", label: "Entrees" },
                { value: "out", label: "Sorties" },
              ]}
            />
            <Select
              style={{ minWidth: 180 }}
              value={filters.category}
              onChange={(value) => handleFilterChange({ category: value })}
              options={[
                { value: "all", label: "Toutes categories" },
                ...manualCategories,
                { value: "payment_customer", label: "Paiements clients" },
                { value: "payment_supplier", label: "Paiements fournisseurs" },
                { value: "transfer_in", label: "Transferts entrants" },
                { value: "transfer_out", label: "Transferts sortants" },
              ]}
            />
            <Input.Search
              placeholder="Reference ou description"
              allowClear
              onSearch={(value) => handleFilterChange({ search: value })}
              onChange={(event) => {
                if (!event.target.value) {
                  handleFilterChange({ search: "" });
                }
              }}
              style={{ minWidth: 220 }}
            />
          </Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            disabled={!permissions.canCreateTreasuryMovements}
          >
            Nouveau mouvement
          </Button>
        </Space>
      </Card>

      <Card>
        <Table
          dataSource={data}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            onChange: handleTableChange,
          }}
          scroll={{ x: "max-content" }}
        />
        <Text type="secondary" className="mt-2 block">
          Resultats: {data.length} / {pagination.total}.
        </Text>
      </Card>

      <Modal
        title="Nouveau mouvement"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleCreate}
        confirmLoading={saving}
        okText="Enregistrer"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="account_id"
            label="Compte"
            rules={[{ required: true, message: "Compte requis" }]}
          >
            <Select placeholder="Selectionner un compte">
              {accounts.map((account) => (
                <Option key={account.id} value={account.id}>
                  {account.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="direction"
            label="Direction"
            rules={[{ required: true, message: "Direction requise" }]}
          >
            <Select>
              <Option value="in">Entree</Option>
              <Option value="out">Sortie</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="category"
            label="Categorie"
            rules={[{ required: true, message: "Categorie requise" }]}
          >
            <Select>
              {manualCategories.map((category) => (
                <Option key={category.value} value={category.value}>
                  {category.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="amount"
            label="Montant"
            rules={[{ required: true, message: "Montant requis" }]}
          >
            <InputNumber style={{ width: "100%" }} min={0} precision={2} />
          </Form.Item>
          <Form.Item name="movement_date" label="Date">
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="reference" label="Reference">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} maxLength={300} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LogisticsTreasuryMovements;
